<template>
  <section :class="voxyTableClasses">
    <div class="voxy-table__header" v-if="$slots.header">
      <slot name="header" />
    </div>
    <div
      :class="['voxy-table__floating-header', rightPinned && 'voxy-table__floating-header_right-pinned']"
      ref="floating-header"
      :style="{ width: tableWidth, top: floatingHeaderTop }"
      v-if="shouldShowFloatingHeader"
    >
      <div class="voxy-table__left" v-if="hasPinnedColumns || selectable">
        <div v-if="scrollTop" :style="placeholderStyle"></div>
        <table ref="floating-table-left" class="voxy-table__table">
          <thead class="voxy-table__head">
            <tr>
              <th class="voxy-table__col-checkbox" v-show="selectable">
                <voxy-checkbox :checked="isAllVisibleItemsSelected" @change="selectAllVisibleItems" />
              </th>
              <th
                v-for="(col, index) in pinnedColumns"
                :class="voxyTableHeaderColumnClasses(col, index)"
                :key="index"
                @click="sortByColumn(col)"
                class="voxy-table__header-column"
              >
                <div class="voxy-table__column-expander">
                  <slot :name="`column.${col.field_name}`">
                    <span>{{ col.label }}</span>
                  </slot>

                  <i v-if="isSortable(col)" :class="voxyTableSortIconClasses(col)" class="voxy-table__sort-icon">
                    <i class="voxy-table__sort-desc">
                      <font-awesome-icon :icon="['fas', 'sort-down']" />
                    </i>
                    <i class="voxy-table__sort-default">
                      <font-awesome-icon :icon="['fas', 'sort']" />
                    </i>
                    <i class="voxy-table__sort-asc">
                      <font-awesome-icon :icon="['fas', 'sort-up']" />
                    </i>
                  </i>
                </div>
              </th>
            </tr>
          </thead>
        </table>
      </div>
      <div class="voxy-table__center" :class="{ 'voxy-table__center_scrolled': isScrolled }">
        <div ref="floating-viewport" class="voxy-table__floating-viewport">
          <table ref="floating-table" class="voxy-table__table">
            <thead class="voxy-table__head">
              <tr>
                <th
                  v-for="(col, index) in centerColumns"
                  :class="voxyTableHeaderColumnClasses(col, index)"
                  :key="index"
                  @click="sortByColumn(col)"
                  class="voxy-table__header-column"
                >
                  <div class="voxy-table__column-expander">
                    <slot :name="`column.${col.field_name}`">
                      <span>{{ col.label }}</span>
                    </slot>
                    <i v-if="isSortable(col)" :class="voxyTableSortIconClasses(col)" class="voxy-table__sort-icon">
                      <i class="voxy-table__sort-desc">
                        <font-awesome-icon :icon="['fas', 'sort-down']" />
                      </i>
                      <i class="voxy-table__sort-default">
                        <font-awesome-icon :icon="['fas', 'sort']" />
                      </i>
                      <i class="voxy-table__sort-asc">
                        <font-awesome-icon :icon="['fas', 'sort-up']" />
                      </i>
                    </i>
                    <help-icon v-if="hasHelpText(col)" class="voxy-table__header-column-help-icon">
                      <p>{{ col.helpText }}</p>
                    </help-icon>
                  </div>
                </th>
              </tr>
            </thead>
          </table>
        </div>
      </div>
    </div>
    <div ref="wrapper" :class="['voxy-table__wrapper', rightPinned && 'voxy-table__wrapper_right-pinned']">
      <div class="voxy-table__left" v-if="hasPinnedColumns || selectable">
        <div v-if="scrollTop" :style="placeholderStyle"></div>
        <table ref="table-fixed-left" class="voxy-table__table voxy-table__table_fixed">
          <thead class="voxy-table__head">
            <tr>
              <th class="voxy-table__col-checkbox" v-show="selectable">
                <voxy-checkbox :checked="isAllVisibleItemsSelected" @change="selectAllVisibleItems" />
              </th>
              <th
                v-for="(col, index) in pinnedColumns"
                :class="voxyTableHeaderColumnClasses(col, index)"
                :key="index"
                @click="sortByColumn(col)"
                class="voxy-table__header-column"
              >
                <slot :name="`column.${col.field_name}`">
                  <span>{{ col.label }}</span>
                </slot>
                <i v-if="isSortable(col)" :class="voxyTableSortIconClasses(col)" class="voxy-table__sort-icon">
                  <i class="voxy-table__sort-desc">
                    <font-awesome-icon :icon="['fas', 'sort-down']" />
                  </i>
                  <i class="voxy-table__sort-default">
                    <font-awesome-icon :icon="['fas', 'sort']" />
                  </i>
                  <i class="voxy-table__sort-asc">
                    <font-awesome-icon :icon="['fas', 'sort-up']" />
                  </i>
                </i>
              </th>
            </tr>
          </thead>
          <tbody id="voxy-table-left-body" class="voxy-table__body">
            <tr
              v-for="(row, rowIndex) in rows"
              :class="voxyTableRowClasses(rowIndex, row)"
              :index="rowIndex"
              :key="getRowId(row, rowIndex)"
              @mouseover="highlightRow(rowIndex)"
              @mouseout="resetHighlightRow"
              :id="'voxy-table-left-row-' + rowIndex"
            >
              <td class="voxy-table__col-checkbox" v-show="selectable">
                <voxy-checkbox v-model="selectedRowsModel" :value="row" :id="getRowId(row)" />
              </td>
              <td
                class="voxy-table__column"
                v-for="(col, columnIndex) in pinnedColumns"
                :class="columnClass(col)"
                :key="columnIndex"
                @click="emitUserClick(col, row)"
              >
                <slot :name="col.field_name" :col="col" :row="row" :value="getRawValue(col, row)">{{
                  getColByRow(col, row)
                }}</slot>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="voxy-table__center" :class="{ 'voxy-table__center_scrolled': isScrolled }">
        <div ref="viewport" :class="['voxy-table__viewport', scrollTop && 'voxy-table__viewport_rotate']">
          <table ref="table" :class="['voxy-table__table', scrollTop && 'voxy-table__table_rotate']" aria-hidden="true">
            <thead class="voxy-table__head">
              <tr>
                <th
                  v-for="(col, index) in centerColumns"
                  :class="voxyTableHeaderColumnClasses(col, index)"
                  :key="index"
                  @click="sortByColumn(col)"
                  class="voxy-table__header-column"
                  :id="'voxy-table-header-column-' + index"
                >
                  <slot :name="`column.${col.field_name}`">
                    <span>{{ col.label }}</span>
                  </slot>
                  <i v-if="isSortable(col)" :class="voxyTableSortIconClasses(col)" class="voxy-table__sort-icon">
                    <i class="voxy-table__sort-desc">
                      <font-awesome-icon :icon="['fas', 'sort-down']" />
                    </i>
                    <i class="voxy-table__sort-default">
                      <font-awesome-icon :icon="['fas', 'sort']" />
                    </i>
                    <i class="voxy-table__sort-asc">
                      <font-awesome-icon :icon="['fas', 'sort-up']" />
                    </i>
                  </i>
                  <help-icon v-if="hasHelpText(col)" class="voxy-table__header-column-help-icon">
                    <p>{{ col.helpText }}</p>
                  </help-icon>
                </th>
              </tr>
            </thead>
            <tbody id="voxy-table-center-body" class="voxy-table__body">
              <tr
                v-for="(row, rowIndex) in rows"
                :class="voxyTableRowClasses(rowIndex, row)"
                :index="rowIndex"
                :key="getRowId(row)"
                @mouseover="highlightRow(rowIndex)"
                @mouseout="resetHighlightRow"
                :id="'voxy-table-center-row-' + rowIndex"
              >
                <td
                  class="voxy-table__column"
                  v-for="(col, columnIndex) in centerColumns"
                  :class="columnClass(col)"
                  :key="columnIndex"
                  @click="emitUserClick(col, row)"
                >
                  <slot :name="col.field_name" :col="col" :row="row" :value="getRawValue(col, row)">
                    {{ getColByRow(col, row) }}
                  </slot>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <footer class="voxy-table__footer" v-show="shouldShowFooter">
      <div class="voxy-table__page-size">
        <span class="voxy-table__page-size-label" test-id="page-size-label">{{ pageSizeLabel }}</span>
        <voxy-select
          v-if="allowChangePageSize"
          class="voxy-table__page-size-options"
          theme="voxy-dark"
          :options="pageSizeOptions"
          v-model="pageSize"
          @input="doChangePageSize"
          required
        ></voxy-select>
      </div>
      <paginate
        v-if="infinity"
        :page-count="infinityPageCount"
        :hide-prev-next="true"
        v-model="page"
        container-class="voxy-table__paginator"
        page-class="voxy-table__paginator-number"
        prev-class="voxy-table__paginator-arrow"
        next-class="voxy-table__paginator-arrow"
        active-class="voxy-table__paginator-number_active"
      >
        <template #prev>
          <font-awesome-icon :icon="['fal', 'angle-left']" />
        </template>
        <template #next>
          <font-awesome-icon :icon="['fal', 'angle-right']" />
        </template>
      </paginate>
      <paginate
        v-else-if="pageCount > 1"
        :page-count="pageCount"
        :hide-prev-next="true"
        v-model="page"
        container-class="voxy-table__paginator"
        page-class="voxy-table__paginator-number"
        prev-class="voxy-table__paginator-arrow"
        next-class="voxy-table__paginator-arrow"
        active-class="voxy-table__paginator-number_active"
      >
        <template #prev>
          <font-awesome-icon :icon="['fal', 'angle-left']" />
        </template>
        <template #next>
          <font-awesome-icon :icon="['fal', 'angle-right']" />
        </template>
      </paginate>
    </footer>
  </section>
</template>

<script>
import moment from 'moment';
import { mapGetters } from 'vuex';
import { VoxyCheckbox, VoxySelect } from '@voxy-team/vue-voxy';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import Paginate from 'vuejs-paginate';
import getScrollableParent from 'scrollparent';

import HelpIcon from '@/components/HelpIcon';
import { number, time } from '@/utils/filters';

function getPercentage(decimal) {
  const percentageNumber = (decimal * 100).toFixed(2);
  return `${percentageNumber}%`;
}

export default {
  name: 'VoxyTable',

  components: {
    FontAwesomeIcon,
    HelpIcon,
    Paginate,
    VoxyCheckbox,
    VoxySelect,
  },

  props: {
    columns: {
      default: () => [],
      type: Array,
    },
    rightPinned: {
      type: Boolean,
      default: false,
    },
    infinity: {
      type: Boolean,
      default: false,
    },
    allowChangePageSize: {
      type: Boolean,
      default: true,
    },
    initialPageSize: {
      type: Number,
      default: 15,
    },
    currentPage: {
      type: Number,
      default: 0,
    },
    displayTotalRows: {
      default: true,
      type: Boolean,
    },
    idField: {
      type: String,
      default: 'id',
    },
    rows: {
      type: Array,
      default: () => [],
    },
    scrollable: {
      type: Boolean,
      default: true,
    },
    selectable: {
      type: Boolean,
      default: false,
    },
    sortable: {
      type: Boolean,
      default: false,
    },
    pagination: {
      type: Boolean,
      default: true,
    },
    totalRows: {
      default: 0,
      type: Number,
    },
    pinLimit: {
      type: Number,
      default: 1,
    },
    rowClasses: {
      type: [String, Function],
      default: null,
    },
    ordering: {
      type: String,
      default: null,
    },
    /**
     * Automatically choose a column to sort by if it is not explicitly specified with `ordering` prop.
     */
    useDefaultOrder: {
      type: Boolean,
      default: true,
    },
    selectedRows: {
      type: Array,
      default: () => [],
    },
    floatingHeader: {
      type: Boolean,
      default: true,
    },
    scrollTop: {
      type: Boolean,
      default: false,
    },
  },

  watch: {
    // whenever rows changes, this function will run
    rows() {
      this.syncSizes();
      this.$nextTick(this.syncHeaders);
    },

    pinnedColumns() {
      this.$nextTick(this.syncPlaceholderHeight);
    },

    isSidebarCollapsed() {
      this.syncPlaceholderHeight();
    },

    shouldShowFloatingHeader() {
      if (this.floatingHeader) {
        this.syncTableWidth();
        this.$nextTick(this.syncHeaders);
      }
    },
  },

  beforeMount() {
    if (!this.ordering && this.useDefaultOrder) {
      this.changeOrdering(this.defaultSortBy);
    }
  },

  mounted() {
    this.scrollableParent = getScrollableParent(this.$el);
    window.addEventListener('resize', this.syncSizes);
    window.addEventListener('scroll', this.onScroll, true);
    this.syncSizes();
  },

  beforeDestroy() {
    window.removeEventListener('scroll', this.onScroll, true);
    window.removeEventListener('resize', this.syncSizes);
  },

  data() {
    return {
      pageSizeOptions: [15, 30, 50, 100],
      paginate: ['rows'],
      pageSize: this.initialPageSize,
      highlightedIndex: -1,
      scrollLeft: 0,
      shouldShowFloatingHeader: false,
      align: 'left',
      scrollableParent: document.body,
      placeholderHeight: '0px',
    };
  },

  computed: {
    ...mapGetters('localSettings', ['isSidebarCollapsed']),

    computedTotal() {
      if (this.infinity) {
        // calculate total count of loaded rows include previous pages
        return this.rows.length + this.currentPage * this.pageSize;
      }

      return this.totalRows || this.rows.length;
    },

    isAllVisibleItemsSelected() {
      return this.selectedRows.length > 0 && this.selectedRows.length === this.rows.length;
    },

    page: {
      get() {
        return this.currentPage + 1;
      },

      set(value) {
        this.$emit('update:selectedRows', []);
        this.$emit('change:currentPage', { currentPage: value - 1 });
      },
    },

    pageCount() {
      return Math.ceil(this.computedTotal / this.pageSize);
    },

    infinityPageCount() {
      return this.rows.length < this.pageSize ? this.page : this.page + 1;
    },

    pageStartItem() {
      return this.currentPage * this.pageSize + 1;
    },

    pageEndItem() {
      return this.computedTotal < this.pageSize
        ? this.computedTotal
        : Math.min((this.currentPage + 1) * this.pageSize, this.computedTotal);
    },

    pageSizeLabel() {
      const label = `Showing rows ${number(this.pageStartItem)} to ${number(this.pageEndItem)}`;

      if (!this.totalRows || !this.displayTotalRows) return label;

      return `${label} of ${number(this.computedTotal)}`;
    },

    shouldShowFooter() {
      return this.computedTotal > 0 && this.pagination;
    },

    voxyTableClasses() {
      return ['voxy-table', !this.scrollable && 'voxy-table_layout_fixed'];
    },

    pinnedColumns() {
      return this.columns.filter(col => col.pinned === true).slice(0, this.pinLimit);
    },

    centerColumns() {
      return this.columns.filter(col => !this.pinnedColumns.includes(col));
    },

    hasPinnedColumns() {
      return this.pinnedColumns.length > 0;
    },

    isScrolled() {
      return (this.hasPinnedColumns || this.selectable) && this.scrollLeft > 0;
    },

    floatingHeaderTop() {
      return `${this.scrollableParent.offsetTop}px`;
    },

    selectedRowsModel: {
      get() {
        return this.selectedRows;
      },

      set(value) {
        this.$emit('update:selectedRows', value);
      },
    },

    sortBy() {
      if (!this.ordering) {
        return this.useDefaultOrder ? this.defaultSortBy : null;
      }

      return this.ordering.startsWith('-') ? this.ordering.slice(1) : this.ordering.slice();
    },

    sortOrder() {
      return this.ordering && this.ordering.startsWith('-') ? 'desc' : 'asc';
    },

    defaultSortBy() {
      if (this.columns.length <= 0) {
        return null;
      }

      const firstSortableColumn = this.columns.find(column => !column.suppressSorting);

      return firstSortableColumn ? firstSortableColumn.field_name : null;
    },

    placeholderStyle() {
      return { height: this.placeholderHeight };
    },
  },

  methods: {
    onScroll() {
      const viewportRect = this.$refs.viewport.getBoundingClientRect();

      this.scrollLeft = this.$refs.viewport.scrollLeft;
      if (this.floatingHeader) {
        this.shouldShowFloatingHeader = viewportRect.top - this.scrollableParent.offsetTop < 0;
      }

      if (this.$refs['floating-viewport']) {
        this.$refs['floating-viewport'].scrollLeft = this.scrollLeft;
      }
    },

    syncHeaders() {
      if (!this.$refs['floating-header']) {
        return;
      }

      const selector = 'voxy-table__header-column';

      if (this.$refs['table-fixed-left']) {
        const pinnedColumns = [...this.$refs['table-fixed-left'].getElementsByClassName(selector)];
        const floatingPinnedColumns = [...this.$refs['floating-table-left'].getElementsByClassName(selector)];

        pinnedColumns.forEach((cell, index) => {
          const width = cell.offsetWidth;
          floatingPinnedColumns[index].style.width = `${width}px`;
        });
      }

      const columns = [...this.$refs.table.getElementsByClassName(selector)];
      const floatingColumns = [...this.$refs['floating-table'].getElementsByClassName('voxy-table__column-expander')];

      columns.forEach((cell, index) => {
        const width = cell.offsetWidth;
        floatingColumns[index].style.width = `${width}px`;
      });
    },

    syncSizes() {
      this.syncTableWidth();
      this.syncPlaceholderHeight();
    },

    syncTableWidth() {
      this.tableWidth = `${this.$refs.wrapper.offsetWidth}px`;
    },

    syncPlaceholderHeight() {
      this.placeholderHeight = `${this.$refs.viewport.offsetHeight - this.$refs.viewport.clientHeight}px`;
    },

    doChangePageSize(pageSizeSelected) {
      this.$emit('change:pagesize', { pageSize: pageSizeSelected });
    },

    getRowId(row, index) {
      return row[this.idField] || index;
    },

    hasHelpText(col) {
      return 'helpText' in col;
    },

    isSortable(col) {
      return this.sortable && !col.suppressSorting;
    },

    isColumnToSort(col) {
      return this.sortable && this.sortBy === col.field_name;
    },

    isHighlightedRow(index) {
      return this.highlightedIndex === index;
    },

    changeOrdering(ordering) {
      this.$emit('change:ordering', { ordering });
    },

    sortByColumn(col) {
      if (!this.isSortable(col)) {
        return;
      }

      const desiredOrdering =
        this.isColumnToSort(col) && this.sortOrder === 'asc' ? `-${col.field_name}` : col.field_name;

      this.changeOrdering(desiredOrdering);
    },

    voxyTableHeaderColumnClasses(col) {
      return {
        'voxy-table__header-column_help': this.hasHelpText(col),
        'voxy-table__header-column_sort': this.isSortable(col),
        'voxy-table__header-column_sorted': this.isColumnToSort(col),
      };
    },

    voxyTableRowClasses(index, row) {
      return [
        'voxy-table__row',
        this.isHighlightedRow(index) && 'voxy-table__row_hover',
        typeof this.rowClasses === 'function' ? this.rowClasses(row) : this.rowClasses,
      ];
    },

    voxyTableSortIconClasses(col) {
      const isColumnToSort = this.isColumnToSort(col);

      return {
        'voxy-table__sort-icon_default': !isColumnToSort,
        'voxy-table__sort-icon_desc': isColumnToSort && this.sortOrder === 'desc',
        'voxy-table__sort-icon_asc': isColumnToSort && this.sortOrder === 'asc',
      };
    },

    selectAllVisibleItems(checked) {
      this.$emit('update:selectedRows', checked ? this.rows.slice() : []);
    },

    getRawValue(col, row) {
      return row[col.source_field];
    },

    getColByRow(col, row) {
      const colType = col.type || '';
      const colRowValue = row[col.source_field];

      if (!colRowValue && colRowValue !== 0) {
        return ' - ';
      }

      if (colRowValue !== ' - ') {
        switch (colType) {
          case 'date':
            return moment(colRowValue).format('ll');
          case 'datetime':
            return moment(colRowValue).format('lll');
          case 'time':
            return time(colRowValue);
          case 'percentage':
            return getPercentage(colRowValue);
          default:
            return colRowValue;
        }
      }

      return colRowValue;
    },

    emitUserClick(col, row) {
      this.$emit(`col-clicked-${col.field_name}`, row);
    },

    columnClass(col) {
      const { align, customClass } = col;
      const columnClass = [];
      if (customClass) {
        columnClass.push(`voxy-table__column_${customClass}`);
      }
      if (align) {
        columnClass.push(`voxy-table__column_${align}`);
      }
      return columnClass;
    },

    highlightRow(index) {
      this.highlightedIndex = index;
    },

    resetHighlightRow() {
      this.highlightedIndex = -1;
    },
  },
};
</script>

<style lang="scss">
@import '~styles/tools/variables';

.voxy-table {
  font-size: 15px;
  width: 100%;

  &_layout_fixed {
    .voxy-table__row th {
      white-space: normal;
    }
  }
}

.voxy-table__viewport {
  width: 100%;
  overflow-x: auto;
  overflow-y: visible;

  &_rotate {
    transform: rotateX(180deg);
  }
}

.voxy-table__floating-viewport {
  width: 100%;
  overflow: hidden;
}

.voxy-table__table {
  width: 100%;
  border-collapse: collapse;

  &_rotate {
    transform: rotateX(180deg);
  }
}

.voxy-table__head,
.voxy-table__body {
  border-bottom: 1px solid transparent;
}

.voxy-table__header-column {
  font-size: 14px;
  font-weight: 600;
  position: relative;
  padding: 12px 10px;
  text-transform: uppercase;
  white-space: nowrap;
  color: $default-text-grey;

  &::after {
    content: '\200b';
  }

  sup {
    font-size: 14px;
    top: -0.125em;
  }

  .voxy-table_layout_fixed & {
    white-space: normal;
  }

  .voxy-table__floating-header & {
    padding-left: 0;
    padding-right: 0;
  }
}

.voxy-table__row td {
  font-size: 1em;
  height: 40px;
  white-space: nowrap;
}

.voxy-table__col-checkbox {
  padding-left: 10px;
  height: 40px;
  text-align: center;
}

.voxy-table__column {
  height: 40px;
  padding: 0 10px;
}

.voxy-table__column-expander {
  padding: 0 10px;
}

.voxy-table__header {
  margin-bottom: 30px;
}

.voxy-table__footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 24px 0;
}

.voxy-table__paginator {
  color: #fff;
  font-size: 1.5em;
  display: flex;
  margin: 0;
  justify-content: flex-end;
  align-items: center;
}

.voxy-table__body {
  overflow: hidden;
}

.voxy-table__row {
  background-color: transparent;
  transition: background-color 0.2s;
}

.voxy-table__row:nth-child(odd) {
  background-color: $default-lighter-grey;
}

.voxy-table__row_hover {
  &,
  &.voxy-table__row:nth-child(odd) {
    background-color: $default-component-grey;
  }
}

.voxy-table__column_no-custom-class {
  color: #fff;
}

.voxy-table__column_email {
  color: #1bccde;
  cursor: pointer;
}

.voxy-table__column_left {
  text-align: left;
}

.voxy-table__column_center {
  text-align: center;
}

.voxy-table__column_right {
  text-align: right;
}

.voxy-table__paginator-arrow {
  font-size: 20px;
  padding: 2px 6px;
  color: $default-grey;

  &:hover {
    color: $default-blue;
  }
}

.voxy-table__paginator-number {
  font-size: 16px;
  min-width: 25px;
  padding: 4px 6px;
  text-align: center;
  cursor: pointer;
  color: $default-disable-text-grey;

  &_active,
  &:hover {
    cursor: unset;
    color: white;
    background-color: rgba(255, 255, 255, 0.31);
    border-radius: 3px;
  }
}

.voxy-table__paginator-number + .voxy-table__paginator-number {
  margin-left: 8px;
}

.voxy-table__paginator-number + .voxy-table__paginator-arrow,
.voxy-table__paginator-arrow + .voxy-table__paginator-number {
  margin-left: 12px;
}

.voxy-table__header-column_sort {
  padding-right: 30px;
  cursor: pointer;
  transition: color 0.2s ease-in-out;
}

.voxy-table__header-column_sort + .voxy-table__header-column_help {
  padding-right: 40px;
}

.voxy-table__header-column_sort:hover {
  color: rgba(white, 0.75);
}

.voxy-table__header-column_sorted {
  color: white;
}

.voxy-table__sort-icon {
  font-size: 14px;
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
}

.voxy-table__sort-desc,
.voxy-table__sort-default,
.voxy-table__sort-asc {
  display: none;
}

.voxy-table__sort-icon_desc .voxy-table__sort-desc {
  display: inline-block;
}

.voxy-table__sort-icon_default .voxy-table__sort-default {
  display: inline-block;
}

.voxy-table__sort-icon_asc .voxy-table__sort-asc {
  display: inline-block;
}

.voxy-table__header-column-help-icon {
  display: inline-block;
  position: relative;
  left: 3px;
  top: -6px;
}

.voxy-table__page-size {
  display: inline-flex;
  align-items: center;
}

.voxy-table__page-size-label {
  font-size: 16px;
  margin-right: 15px;
  color: $default-grey;
}

.voxy-table__page-size-options {
  max-width: 100px;
}

.voxy-table__wrapper {
  display: flex;

  &_right-pinned {
    flex-direction: row-reverse;
  }
}

.voxy-table__floating-header {
  display: flex;
  position: fixed;
  z-index: 100;
  top: 0;
  background: $night-rider-grey;
  box-shadow: 0 2px 4px rgba(black, 0.25);

  &_right-pinned {
    flex-direction: row-reverse;
  }
}

.voxy-table__left {
  flex: 0 0 auto;
}

.voxy-table__center {
  flex: auto;
  overflow-x: hidden;
  position: relative;

  &_scrolled {
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      height: 100%;
      width: 6px;
      background: linear-gradient(to right, rgba(black, 0.25), transparent);
    }
  }
}
</style>
